import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { z } from 'zod'
import { FormContainer } from '@/components/form/form-container'
import { useFormConfig } from '@/hooks/use-form-config'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

// Search params schema for single-page mode
const searchParamsSchema = z.object({
  long: z.coerce.boolean().optional().catch(undefined),
})

export const Route = createFileRoute('/$organizationId')({
  component: InquiryFormPage,
  validateSearch: (search) => searchParamsSchema.parse(search),
})

function InquiryFormPage() {
  const { organizationId } = Route.useParams()
  const { long: singlePage } = Route.useSearch()
  const { config, isLoading, error, refetch } = useFormConfig(organizationId)

  // Fall back for transient/config load failures, but show a clear not-found state.
  const isFallbackMode = !isLoading && !config && !!error && error !== 'Organization not found'

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-start bg-transparent p-4">
        <div className="w-full max-w-2xl mx-auto">
          <div className="rounded-xl border border-border/50 bg-card/95 backdrop-blur-sm shadow-lg shadow-black/[0.03]">
            {/* Progress Header Skeleton */}
            <div className="border-b border-border/50 bg-muted/30 px-6 py-5 sm:px-8 rounded-t-xl">
              <div className="relative">
                {/* Connection line background */}
                <div className="absolute top-5 left-0 right-0 h-[2px] bg-border mx-10" />
                
                {/* Step indicators */}
                <div className="relative flex justify-between">
                  {[
                    { label: 'Event Details', width: 'w-24' },
                    { label: 'Event Information', width: 'w-32' },
                    { label: 'Add-ons', width: 'w-20' },
                    { label: 'Contact', width: 'w-20' },
                  ].map((step, i) => (
                    <div key={i} className="flex flex-col items-center gap-2">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <Skeleton className={`h-3 ${step.width} hidden sm:block`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Form Content Skeleton */}
            <div className="p-6 sm:p-8">
              <div className="min-h-[320px] space-y-8">
                {/* Title */}
                <div className="space-y-1">
                  <Skeleton className="h-7 w-48" />
                  <Skeleton className="h-4 w-72" />
                </div>

                {/* Form Fields */}
                <div className="space-y-6">
                  <div className="grid gap-6 sm:grid-cols-2 sm:items-start">
                    <div className="flex flex-col space-y-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-9 w-full" />
                      <div className="min-h-[20px]" />
                    </div>
                    <div className="flex flex-col space-y-2">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-9 w-full" />
                      <div className="min-h-[20px]" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-4 w-64" />
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Footer Skeleton */}
            <div className="border-t border-border/50 bg-muted/20 px-6 py-4 sm:px-8 rounded-b-xl">
              <div className="flex items-center justify-between gap-4">
                <Skeleton className="h-10 w-20" />
                <Skeleton className="h-10 w-24" />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // In fallback mode, render the form without config
  if (isFallbackMode) {
    return (
      <div className="min-h-screen flex items-start bg-transparent p-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-2xl mx-auto"
        >
          <FormContainer
            organizationId={organizationId}
            config={null}
            isFallbackMode={true}
            singlePage={singlePage}
          />
        </motion.div>
      </div>
    )
  }

  // Show error screen for not-found or missing configuration states.
  if (error || !config) {
    return (
      <div className="flex min-h-[400px] items-center justify-center bg-transparent p-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="rounded-xl border border-destructive/20 bg-card/95 backdrop-blur-sm shadow-lg p-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">
              Unable to Load Form
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {error || 'The form configuration could not be loaded. Please try again.'}
            </p>
            <Button
              onClick={() => refetch()}
              variant="outline"
              className="mt-6 gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </Button>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-start bg-transparent p-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-2xl mx-auto"
      >
        <FormContainer
          organizationId={organizationId}
          config={config}
          singlePage={singlePage}
        />
      </motion.div>
    </div>
  )
}
